/**
 * Sewing Factory Board — the floor panels, one per Building-Floor. See `design-notes.md`.
 *
 * Figures on the left, the floor's line chips on the right. Every colour comes from a category the
 * server assigned, read through `tokens.ts`; no threshold and no target comparison lives here (§5.2).
 * Inline `style` carries bar geometry only — §4.8's one exception.
 *
 * The chips are a client-side regrouping of the `lines` view and are therefore gated on
 * `linesComplete` (§4.3); see `groupLines` below.
 *
 * **Contract with `fit.ts`:** every panel's grid child carries `data-floor-panel`, and its first
 * child is the content wrapper the hook measures — `grid-auto-rows: 1fr` stretches the panel, so
 * only the wrapper reports the height the content actually wants.
 */

import { faTrophy } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMemo, type JSX } from 'react';
import { Link } from 'react-router-dom';

import { reportPath } from '@/lib/menu';
import { cn } from '@/lib/utils';

import { lineStatusLabel } from '../sewing-line-status';
import {
  PODIUM,
  PODIUM_BORDER,
  count,
  effTone,
  lineTone,
  num,
  pct,
  str,
  wftTone,
  widthPct,
  wipTone,
  type FactoryRow,
  type Tier,
} from './tokens';

interface Scale {
  /** Gap between panels. */
  grid: string;
  /** The content wrapper's padding, and the gap between the panel header and its body. */
  panel: string;
  /** Gap between the figures column and the chips column. */
  gap: string;
  floorLabel: string;
  medal: string;
  note: string;
  figure: string;
  target: string;
  track: string;
  pill: string;
  statLabel: string;
  statValue: string;
  statAgainst: string;
  /**
   * The chip grid, as `auto-fit` over a minimum chip width. A fixed column count fails at both
   * ends of the real data — BVG floors carry 4 chips and PGV floors carry 23.
   */
  chipGrid: string;
  chip: string;
  chipId: string;
  chipValue: string;
}

/**
 * The board's densities; the measuring hook picks one. Prebuilt class sets, never a computed font
 * size — §4.8 allows runtime `style` for geometry only.
 */
const SCALE: Record<Tier, Scale> = {
  // The 1920x1080 wall, two panels across.
  xl: {
    grid: 'gap-4',
    panel: 'gap-3 p-5',
    gap: 'gap-5',
    floorLabel: 'text-4xl',
    medal: 'size-7',
    note: 'text-xl',
    figure: 'text-6xl',
    target: 'text-xl',
    track: 'h-6 rounded-full',
    pill: 'px-3 py-1 text-base',
    // Output / WFT / WIP stack — wall-readable; was a step smaller than the big efficiency figure
    statLabel: 'text-base tracking-widest',
    statValue: 'text-3xl',
    statAgainst: 'text-lg',
    chipGrid: 'grid-cols-[repeat(auto-fit,minmax(5rem,1fr))]',
    chip: 'gap-0.5 rounded-lg px-2 py-2',
    chipId: 'text-xl',
    chipValue: 'text-base',
  },
  lg: {
    grid: 'gap-3',
    panel: 'gap-2.5 p-4',
    gap: 'gap-4',
    floorLabel: 'text-3xl',
    medal: 'size-6',
    note: 'text-lg',
    figure: 'text-5xl',
    target: 'text-lg',
    track: 'h-5 rounded-full',
    pill: 'px-2.5 py-0.5 text-sm',
    statLabel: 'text-sm tracking-widest',
    statValue: 'text-2xl',
    statAgainst: 'text-base',
    chipGrid: 'grid-cols-[repeat(auto-fit,minmax(4.5rem,1fr))]',
    chip: 'gap-0.5 rounded-md px-1.5 py-1.5',
    chipId: 'text-lg',
    chipValue: 'text-sm',
  },
  md: {
    grid: 'gap-3',
    panel: 'gap-2 p-3',
    gap: 'gap-3',
    floorLabel: 'text-2xl',
    medal: 'size-5',
    note: 'text-base',
    figure: 'text-4xl',
    target: 'text-base',
    track: 'h-4 rounded-full',
    pill: 'px-2 py-0.5 text-xs',
    statLabel: 'text-sm tracking-widest',
    statValue: 'text-xl',
    statAgainst: 'text-sm',
    chipGrid: 'grid-cols-[repeat(auto-fit,minmax(4rem,1fr))]',
    chip: 'rounded-md px-1.5 py-1',
    chipId: 'text-base',
    chipValue: 'text-xs',
  },
  // Three panels across, so a chip gets narrower before the type gives up a size.
  sm: {
    grid: 'gap-2',
    panel: 'gap-1.5 p-2.5',
    gap: 'gap-2.5',
    floorLabel: 'text-xl',
    medal: 'size-4',
    note: 'text-sm',
    figure: 'text-3xl',
    target: 'text-sm',
    track: 'h-3 rounded-full',
    pill: 'px-1.5 text-xs',
    statLabel: 'text-xs tracking-widest',
    statValue: 'text-lg',
    statAgainst: 'text-sm',
    chipGrid: 'grid-cols-[repeat(auto-fit,minmax(3.25rem,1fr))]',
    chip: 'rounded-sm px-1 py-1',
    chipId: 'text-sm',
    chipValue: 'text-xs',
  },
  // At a desk the board is a report again, at report density.
  desk: {
    grid: 'gap-3',
    panel: 'gap-2.5 p-4',
    gap: 'gap-4',
    floorLabel: 'text-xl',
    medal: 'size-4',
    note: 'text-base',
    figure: 'text-3xl',
    target: 'text-sm',
    track: 'h-4 rounded-full',
    pill: 'px-2 py-0.5 text-xs',
    statLabel: 'text-sm tracking-widest',
    statValue: 'text-lg',
    statAgainst: 'text-sm',
    chipGrid: 'grid-cols-[repeat(auto-fit,minmax(4rem,1fr))]',
    chip: 'rounded-md px-1.5 py-1',
    chipId: 'text-sm',
    chipValue: 'text-xs',
  },
};

const DEFAULT_GRID = 'grid-cols-1 md:grid-cols-2';

/** One column below tablet (§4.9); the tier's count applies from there up. Prebuilt — a class name cannot be computed (§4.8). */
const GRID_COLUMNS: Record<number, string> = {
  1: 'grid-cols-1',
  2: DEFAULT_GRID,
  3: 'grid-cols-1 md:grid-cols-3',
  4: 'grid-cols-1 md:grid-cols-4',
};

/** A separator no code can contain — `-` cannot be used, BVN floors are literally `K5-F2` and BCA buildings are `A`/`B`. */
const KEY_SEP = '\x00';

function floorKey(row: FactoryRow): string {
  return `${str(row, 'building')}${KEY_SEP}${str(row, 'floor')}`;
}

/**
 * The lines of each floor, or `null` when the lines result was capped.
 *
 * Regrouping a complete result for presentation is permitted (§4.3); regrouping a capped one is not,
 * because a partial bucket would render as though it were the floor's whole line-up.
 */
function groupLines(lines: FactoryRow[], linesComplete: boolean): Map<string, FactoryRow[]> | null {
  if (!linesComplete) return null; // §4.3 completeness gate — a partial set of chips must never look like the floor's lines
  const map = new Map<string, FactoryRow[]>();
  for (const line of lines) {
    const key = floorKey(line);
    const bucket = map.get(key);
    if (bucket === undefined) {
      map.set(key, [line]);
    } else {
      bucket.push(line);
    }
  }
  return map;
}

/**
 * Line counts by composite `line_status` — same CASE and words as the floor board STATUS pill and
 * the line chips. Zero counts are hidden so a floor only shows statuses it actually has.
 * Precedence order matches the SQL CASE (flow-first).
 *
 * `lines_on_target` / `lines_with_target` remain a different population (own plan only).
 */
const COUNT_PILLS: readonly { field: string; status: string }[] = [
  { field: 'status_not_running', status: 'NOT_RUNNING' },
  { field: 'status_starved', status: 'STARVED' },
  { field: 'status_pace_and_quality', status: 'PACE_AND_QUALITY' },
  { field: 'status_quality', status: 'QUALITY' },
  { field: 'status_no_plan', status: 'NO_PLAN' },
  { field: 'status_below_target', status: 'BELOW_TARGET' },
  { field: 'status_near_target', status: 'NEAR_TARGET' },
  { field: 'status_wip_excess', status: 'WIP_EXCESS' },
  { field: 'status_on_target', status: 'ON_TARGET' },
];

/** A figure and what it is measured against — §4.6: a figure with a target shows it. */
function Stat({
  label,
  value,
  against,
  tone,
  s,
}: {
  label: string;
  value: string;
  against: string;
  tone: string;
  s: Scale;
}) {
  return (
    <div className="flex min-w-0 items-baseline gap-2">
      <span className={cn('shrink-0 font-semibold text-muted-foreground uppercase', s.statLabel)}>
        {label}
      </span>
      <span className={cn('shrink-0 font-bold tabular-nums', s.statValue, tone)}>{value}</span>
      <span className={cn('truncate text-muted-foreground tabular-nums', s.statAgainst)}>
        {against}
      </span>
    </div>
  );
}

/** The output figure's denominator: the elapsed-share target where the server sent one, else the whole-day plan. */
function outputAgainst(output: number | null, soFar: number | null, target: number | null): string {
  if (soFar !== null) return `${count(output)} / ${count(soFar)} so far`;
  if (target !== null) return `${count(output)} / ${count(target)} pcs`;
  return `${count(output)} pcs`;
}

function LineChip({ line, s }: { line: FactoryRow; s: Scale }) {
  const tone = lineTone(line);
  // An idle line reads `—`: its 0.0% is an absence, not a measurement.
  const efficiency = line['is_running'] === true ? num(line, 'efficiency') : null;
  const days = num(line, 'wip_cover_days');
  const status = str(line, 'line_status');
  const statusWords = lineStatusLabel(status);
  // Top 3 on this floor by server rank (1=best) — gold/silver/bronze border, not a trophy (kiosk overlap).
  const lineRank = num(line, 'floor_efficiency_rank');
  const isLeader = lineRank !== null && lineRank >= 1 && lineRank <= 3;
  const podiumBorder = isLeader ? PODIUM_BORDER[lineRank] : undefined;

  return (
    <div
      className={cn(
        'flex min-w-0 flex-col items-center overflow-hidden text-center leading-tight',
        s.chip,
        tone.wash,
        podiumBorder,
      )}
      // Hover/title carries the composite verdict the floor board shows as a STATUS pill; the
      // tint already uses line_status, but a nine-name vocabulary will not fit on the wall chip.
      title={isLeader ? `Rank ${String(lineRank)}. ${statusWords}` : statusWords}
    >
      {isLeader ? <span className="sr-only">{`Rank ${String(lineRank)}. `}</span> : null}
      <span className={cn('w-full truncate font-bold', s.chipId)}>{str(line, 'line_label')}</span>
      <span className="sr-only">{`${statusWords}. `}</span>
      <span className={cn('tabular-nums', s.chipValue)}>
        <span className="sr-only">Efficiency </span>
        {pct(efficiency)}
      </span>
      <span className={cn('text-muted-foreground tabular-nums', s.chipValue)}>
        <span className="sr-only">WIP cover </span>
        {days === null ? '—' : `${days.toFixed(1)}d`}
      </span>
    </div>
  );
}

function LineChips({ chips, s }: { chips: FactoryRow[] | null; s: Scale }) {
  if (chips === null) {
    return (
      <p className={cn('text-muted-foreground', s.note)}>
        Line detail is hidden because the lines result was capped. Open a floor board to see its
        lines.
      </p>
    );
  }
  if (chips.length === 0) {
    return <p className={cn('text-muted-foreground', s.note)}>No line worked on this floor.</p>;
  }
  // `flex-1 min-h-0`: the chips are the part that gives way, so a 23-line floor never grows its panel.
  return (
    <div className={cn('grid min-h-0 flex-1 content-start gap-1 overflow-hidden', s.chipGrid)}>
      {chips.map((line) => (
        <LineChip key={str(line, 'sfts_line')} line={line} s={s} />
      ))}
    </div>
  );
}

function FloorPanel({
  floor,
  chips,
  s,
  isKiosk,
  linkParams,
}: {
  floor: FactoryRow;
  chips: FactoryRow[] | null;
  s: Scale;
  isKiosk: boolean;
  linkParams: { factory: string; tran_date: string; metric: string };
}) {
  const tone = effTone(floor);
  const building = str(floor, 'building');
  const floorCode = str(floor, 'floor');
  const label = str(floor, 'floor_label') || `${building}-${floorCode}`;
  const efficiency = num(floor, 'avg_efficiency');
  const target = num(floor, 'effective_target') ?? num(floor, 'efficiency_target');
  const rank = num(floor, 'floor_rank');
  const podium = rank === null ? undefined : PODIUM[rank];
  const wipDays = num(floor, 'wip_cover_days');
  const wftLimit = num(floor, 'wft_limit');

  const body = (
    // The panel's FIRST child, and the box `fit.ts` measures: `auto-rows-fr` stretches the panel
    // itself, so its border box carries slack that is not content. Padding lives here for that reason.
    <div className={cn('flex min-h-0 flex-col', s.panel)}>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className={cn('leading-none font-bold', s.floorLabel, tone.text)}>{label}</span>
        {podium === undefined ? null : (
          <>
            {/* Decorative, so the rank is repeated as text — colour alone is never the signal (§4.6). */}
            <FontAwesomeIcon icon={faTrophy} className={cn('shrink-0', podium, s.medal)} aria-hidden />
            <span className="sr-only">{`Rank ${String(rank)} in the factory. `}</span>
          </>
        )}
        <span className={cn('text-muted-foreground', s.note)}>
          {`${count(num(floor, 'running_line_count'))} of ${count(num(floor, 'line_count'))} lines running`}
        </span>
      </div>

      {/* Stacked below tablet, side by side from there up (§4.9). */}
      <div className={cn('flex min-h-0 flex-1 flex-col md:flex-row', s.gap)}>
        {/*
          Left column: metrics must not shrink under kiosk height pressure — otherwise the
          absolute bar fill and the status pills stack on top of each other (BCA 6-up).
        */}
        <div className="flex min-w-0 shrink-0 flex-col gap-2 md:w-[48%] md:max-w-md">
          {/* Efficiency block: own shrink-0 stack so bar and pills never share a compressed line */}
          <div className="flex shrink-0 flex-col gap-2">
            <div className="flex items-baseline gap-2">
              <span className={cn('leading-none font-bold tabular-nums', s.figure, tone.text)}>
                {pct(efficiency)}
              </span>
              <span className={cn('text-muted-foreground tabular-nums', s.target)}>
                {target === null ? '' : `tgt ${target.toFixed(0)}%`}
              </span>
            </div>

            {/* overflow-hidden clips the fill; tick stays inside the track (no -inset-y bleed). */}
            <div className={cn('relative w-full overflow-hidden bg-muted', s.track)}>
              <div
                className={cn('absolute inset-y-0 left-0', tone.fill)}
                style={{ width: widthPct(efficiency) }}
              />
              {target === null ? null : (
                <div
                  className="absolute inset-y-0 z-10 w-0.5 bg-foreground/80"
                  style={{ left: widthPct(target) }}
                  aria-hidden
                />
              )}
            </div>

            <div className="flex flex-wrap gap-1">
              {COUNT_PILLS.map((bucket) => {
                const value = num(floor, bucket.field);
                if (value === null || value === 0) return null; // an empty bucket is noise on a wall
                return (
                  <span
                    key={bucket.field}
                    className={cn(
                      'rounded-md font-semibold whitespace-nowrap',
                      s.pill,
                      lineTone({ line_status: bucket.status }).wash,
                    )}
                  >
                    {`${count(value)} ${lineStatusLabel(bucket.status)}`}
                  </span>
                );
              })}
            </div>
          </div>

          <div className="flex min-h-0 flex-col gap-1.5">
            <Stat
              s={s}
              label="Output"
              value={pct(num(floor, 'day_output_pct'), 0)}
              against={outputAgainst(
                num(floor, 'day_output_pcs'),
                num(floor, 'day_output_target_so_far'),
                num(floor, 'day_output_target_pcs'),
              )}
              tone=""
            />
            <Stat
              s={s}
              label="WFT"
              value={pct(num(floor, 'avg_wft'))}
              against={wftLimit === null ? '' : `/ ${wftLimit.toFixed(1)}% limit`}
              tone={wftTone(floor).text}
            />
            <Stat
              s={s}
              label="WIP"
              value={wipDays === null ? '—' : `${wipDays.toFixed(1)}d`}
              against={`${count(num(floor, 'wip_pcs'))} pcs`}
              tone={wipTone(floor).text}
            />
          </div>
        </div>

        <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-1 overflow-hidden">
          <span className={cn('shrink-0 font-semibold text-muted-foreground uppercase', s.statLabel)}>
            Lines
          </span>
          <LineChips chips={chips} s={s} />
        </div>
      </div>
    </div>
  );

  const frame = cn('flex min-h-0 flex-col overflow-hidden rounded-lg border-2 bg-card', tone.edge);

  // A wall screen has nobody to click it, so kiosk gets no link at all (§4.9 — a focus stop that cannot be used).
  if (isKiosk) {
    return (
      <div data-floor-panel className={frame}>
        {body}
      </div>
    );
  }

  const params = new URLSearchParams({
    factory: str(floor, 'factory') || linkParams.factory,
    building,
    floor: floorCode,
    tran_date: linkParams.tran_date,
    metric: linkParams.metric,
  });

  return (
    <Link
      data-floor-panel
      to={`${reportPath('sewing-floor-board')}?${params.toString()}`}
      aria-label={`Open the ${label} floor board`}
      className={cn(
        frame,
        'outline-none transition-colors hover:bg-accent/40',
        'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
      )}
    >
      {body}
    </Link>
  );
}

export function FloorPanels({
  floors,
  lines,
  linesComplete,
  isLoading,
  tier,
  columns,
  isKiosk,
  linkParams,
}: {
  floors: FactoryRow[];
  lines: FactoryRow[];
  /** True only when the lines response returned everything — the chips are gated on it (§4.3). */
  linesComplete: boolean;
  isLoading: boolean;
  tier: Tier;
  columns: number;
  isKiosk: boolean;
  /** factory, tran_date and metric, for the drill-through link. */
  linkParams: { factory: string; tran_date: string; metric: string };
}): JSX.Element {
  const s = SCALE[tier];
  const linesByFloor = useMemo(() => groupLines(lines, linesComplete), [lines, linesComplete]);

  const grid = cn('grid min-h-0 flex-1 auto-rows-fr', GRID_COLUMNS[columns] ?? DEFAULT_GRID, s.grid);

  if (isLoading) {
    return (
      <div className={grid}>
        {Array.from({ length: columns * 2 }, (_, index) => (
          <div key={index} className="min-h-40 rounded-lg bg-muted motion-safe:animate-pulse" />
        ))}
      </div>
    );
  }

  if (floors.length === 0) {
    return (
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 rounded-lg border border-border p-8 text-center">
        <p className="font-semibold text-foreground">No floor worked on the selected day.</p>
        <p className="text-muted-foreground">
          Pick another day, or another factory — a floor appears here once one of its lines has
          output.
        </p>
      </div>
    );
  }

  return (
    <div className={grid}>
      {floors.map((floor) => (
        <FloorPanel
          key={floorKey(floor)}
          floor={floor}
          chips={linesByFloor === null ? null : (linesByFloor.get(floorKey(floor)) ?? [])}
          s={s}
          isKiosk={isKiosk}
          linkParams={linkParams}
        />
      ))}
    </div>
  );
}
